import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle, Loader2, ArrowLeft, Calculator, TrendingUp, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SEO } from "@/components/SEO";
import { serviceJsonLd } from "@/lib/seo";

import { getLeadMeta } from "@/lib/tracking";

type FormData = {
  name: string;
  position: string;
  company: string;
  currentSituation: string;
  perspective: string;
  privacyAccepted: boolean;
};

const BusinessCase = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();
  const c = t.businessCase;

  const formSchema = z.object({
    name: z.string().trim().min(2, c.validation.nameMin).max(100),
    position: z.string().trim().min(2, c.validation.positionMin).max(100),
    company: z.string().trim().min(2, c.validation.companyMin).max(150),
    currentSituation: z.string().trim().min(10, c.validation.situationMin).max(2000),
    perspective: z.string().trim().min(10, c.validation.perspectiveMin).max(2000),
    privacyAccepted: z.literal(true, {
      errorMap: () => ({ message: c.validation.privacyRequired }),
    }),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      position: "",
      company: "",
      currentSituation: "",
      perspective: "",
      privacyAccepted: false as unknown as true,
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    const { error } = await supabase.from("business_case_requests").insert({
      name: data.name,
      position: data.position,
      company: data.company,
      current_situation: data.currentSituation,
      perspective: data.perspective,
      privacy_accepted: data.privacyAccepted,
      ...getLeadMeta(),
    });
    setIsLoading(false);
    if (error) {
      toast({
        title: c.toast.errorTitle,
        description: c.toast.errorDescription,
        variant: "destructive",
      });
      return;
    }
    setIsSubmitted(true);
    toast({ title: c.toast.successTitle, description: c.toast.successDescription });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={t.seo.businessCase.title}
        description={t.seo.businessCase.description}
        path="/business-case"
        jsonLd={serviceJsonLd({
          name: t.seo.businessCase.title,
          description: t.seo.businessCase.description,
          path: "/business-case",
        })}
      />

      <LanguageSwitcher />

      {/* Hero */}
      <section className="relative bg-primary text-primary-foreground py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
        <div className="container relative px-4 max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {c.backToReport}
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/20 text-accent border border-accent/30 mb-6">
              <Calculator className="h-4 w-4" />
              <span className="text-sm font-medium">{c.badge}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl font-bold leading-tight mb-6">
              {c.heroTitle}
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/85 leading-relaxed">
              {c.heroSubtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro + Benefits */}
      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-lg text-foreground/80 leading-relaxed mb-6">{c.intro1}</p>
            <p className="text-lg text-foreground/80 leading-relaxed mb-12">{c.intro2}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Users, title: c.benefits[0].title, desc: c.benefits[0].desc },
              { icon: Calculator, title: c.benefits[1].title, desc: c.benefits[1].desc },
              { icon: TrendingUp, title: c.benefits[2].title, desc: c.benefits[2].desc },
            ].map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-card border border-border rounded-2xl p-6"
              >
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <b.icon className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-serif text-xl font-bold text-foreground mb-2">{b.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 bg-muted/30">
        <div className="container px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              {c.formTitle}
            </h2>
            <p className="text-lg text-muted-foreground">{c.formSubtitle}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-card rounded-2xl border border-border p-8 md:p-10 shadow-lg"
          >
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-report-success/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-report-success" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-foreground mb-3">
                  {c.success.title}
                </h3>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  {c.success.description}
                </p>
                <Button asChild variant="outline">
                  <Link to="/">{c.success.backButton}</Link>
                </Button>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{c.fields.name} *</FormLabel>
                          <FormControl>
                            <Input placeholder={c.fields.namePlaceholder} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="position"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{c.fields.position} *</FormLabel>
                          <FormControl>
                            <Input placeholder={c.fields.positionPlaceholder} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{c.fields.company} *</FormLabel>
                        <FormControl>
                          <Input placeholder={c.fields.companyPlaceholder} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="currentSituation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{c.fields.situation} *</FormLabel>
                        <FormControl>
                          <Textarea
                            rows={4}
                            placeholder={c.fields.situationPlaceholder}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="perspective"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{c.fields.perspective} *</FormLabel>
                        <FormControl>
                          <Textarea
                            rows={4}
                            placeholder={c.fields.perspectivePlaceholder}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="privacyAccepted"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start gap-3 rounded-lg border border-border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="mt-0.5"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-tight">
                          <FormLabel className="text-sm font-normal cursor-pointer">
                            {c.fields.privacy}
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground py-6 text-base"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {c.fields.submitting}
                      </>
                    ) : (
                      c.fields.submit
                    )}
                  </Button>
                </form>
              </Form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BusinessCase;
